import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { KPICard } from '@/components/KPICard';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Calendar, Crown, Plane, AlertCircle, Clock, FileWarning, MapPin, ArrowRight, Sparkles, Building2, Globe, Coffee, Briefcase, HelpCircle } from 'lucide-react';
import { fmtDateTime, fmtTime, fmtDay } from '@/lib/dates';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import SPPDMap from '@/components/SPPDMap';

export default function ExecutiveCommandCenter() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/summary').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="py-16 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const c = data.cards;
  const statusColors = { Approved: '#10B981', Pending: '#F59E0B', Rejected: '#EF4444', 'Sedang Berlangsung': '#0EA5E9', 'Sedang Dinas': '#0EA5E9', 'Belum Berangkat': '#94A3B8', Selesai: '#10B981' };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden hero-warm border border-slate-200 shadow-premium">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#DC2626] via-[#FBBF24] to-[#DC2626]" />
        <div className="p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-slate-200 text-xs font-semibold text-slate-700 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#DC2626]" />
              Executive Command Center · PROAKTIF
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Selamat datang, <span className="text-[#DC2626]">{user.full_name.split(' ').slice(0, 2).join(' ')}</span>
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Berikut ringkasan keprotokolan dan perjalanan dinas pejabat Pemkot Makassar secara real-time. Anda memiliki <strong>{c.agenda_hari_ini}</strong> agenda hari ini dan <strong>{c.pejabat_sedang_dinas}</strong> pejabat sedang menjalankan tugas dinas.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:items-end w-full sm:w-auto">
            <Link to="/agenda/kalender"><Button variant="outline" className="w-full sm:w-auto border-slate-300"><Calendar className="w-4 h-4 mr-2" />Lihat Kalender</Button></Link>
            <Link to="/sppd"><Button className="w-full sm:w-auto bg-[#DC2626] hover:bg-[#B91C1C]"><Plane className="w-4 h-4 mr-2" />Monitoring SPPD</Button></Link>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard label="Agenda Hari Ini" value={c.agenda_hari_ini} icon={Calendar} accent="red" sublabel="Kegiatan terjadwal" />
        <KPICard label="Agenda Mendesak" value={c.agenda_mendesak} icon={AlertCircle} accent="gold" sublabel="Perlu perhatian segera" />
        <KPICard label="Sedang Dinas DN" value={c.pejabat_sedang_dinas_dn || 0} icon={Plane} accent="blue" sublabel="Dinas Dalam Negeri" />
        <KPICard label="Sedang Dinas LN" value={c.pejabat_sedang_dinas_ln || 0} icon={Plane} accent="red" sublabel="Dinas Luar Negeri" />
        <KPICard label="Menunggu Validasi" value={c.draft_pending} icon={FileWarning} accent="slate" sublabel="Draft & approval" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today + Tomorrow agenda */}
        <Card className="lg:col-span-2 border-slate-200 shadow-premium">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold">Timeline Agenda</CardTitle>
              <Link to="/agenda" className="text-xs font-semibold text-[#DC2626] hover:underline inline-flex items-center gap-1">Semua Agenda <ArrowRight className="w-3 h-3" /></Link>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="today">
              <TabsList>
                <TabsTrigger value="today" data-testid="tab-today">Hari Ini ({data.today_agendas.length})</TabsTrigger>
                <TabsTrigger value="tomorrow" data-testid="tab-tomorrow">Besok ({data.tomorrow_agendas.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="today" className="mt-4">
                <AgendaTimelineList items={data.today_agendas} />
              </TabsContent>
              <TabsContent value="tomorrow" className="mt-4">
                <AgendaTimelineList items={data.tomorrow_agendas} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Pejabat Sedang Dinas */}
        <Card className="border-slate-200 shadow-premium">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold">Pejabat Sedang Dinas</CardTitle>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-50 px-2 py-1 rounded-full">
                <span className="w-1.5 h-1.5 bg-sky-500 rounded-full pulse-dot" /> Live
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {data.sedang_dinas.length === 0 ? (
              <EmptyState title="Tidak ada pejabat sedang dinas" description="Semua pejabat berada di Makassar." />
            ) : (
              <div className="space-y-3">
                {data.sedang_dinas.map((s) => (
                  <Link key={s.id} to={`/sppd/${s.id}`} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
                      <Plane className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-800 truncate">{s.pejabat_nama}</div>
                      <div className="text-xs text-slate-500 truncate">{s.jabatan}</div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-600"><MapPin className="w-3 h-3" /> {s.tujuan_kota}, {s.tujuan_provinsi}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card className="border-slate-200 shadow-premium" data-testid="sppd-map-card">
        <CardHeader>
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#DC2626]" /> Peta Sebaran Perjalanan Dinas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SPPDMap items={data.sedang_dinas} />
        </CardContent>
      </Card>

      {/* Keberadaan Pejabat Widget */}
      <KeberadaanWidget />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-charts">
        <Card className="lg:col-span-2 border-slate-200 shadow-premium">
          <CardHeader>
            <CardTitle className="text-base font-bold">Statistik Agenda 6 Bulan Terakhir</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_agenda}>
                <defs>
                  <linearGradient id="areaRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#DC2626" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="bulan" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0' }} />
                <Area type="monotone" dataKey="jumlah" stroke="#DC2626" strokeWidth={2.5} fill="url(#areaRed)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader>
            <CardTitle className="text-base font-bold">Status SPPD</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.sppd_status} dataKey="jumlah" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                  {data.sppd_status.map((entry, idx) => (
                    <Cell key={idx} fill={statusColors[entry.status] || '#94A3B8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-premium">
          <CardHeader>
            <CardTitle className="text-base font-bold">Statistik SPPD 6 Bulan</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_sppd}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="bulan" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="jumlah" fill="#FBBF24" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader>
            <CardTitle className="text-base font-bold">Distribusi Delegasi Agenda</CardTitle>
          </CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.delegasi_chart} dataKey="jumlah" nameKey="tipe" outerRadius={90} label>
                  <Cell fill="#DC2626" />
                  <Cell fill="#FBBF24" />
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KeberadaanWidget() {
  const [data, setData] = React.useState({ counts: {}, pejabat: [] });
  React.useEffect(() => {
    let mounted = true;
    const load = () => {
      import('@/lib/api').then(({ api }) => {
        api.get('/pejabat/keberadaan').then((r) => { if (mounted) setData(r.data); }).catch(() => {});
      });
    };
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const STATUS_META = {
    'Di Kantor': { icon: Building2, color: 'emerald' },
    'Sedang Agenda': { icon: Calendar, color: 'sky' },
    'Sedang Dinas DN': { icon: Plane, color: 'amber' },
    'Sedang Dinas LN': { icon: Globe, color: 'red' },
    'Cuti': { icon: Coffee, color: 'slate' },
    'Tidak Diketahui': { icon: HelpCircle, color: 'slate' },
  };
  const colorCls = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return (
    <Card className="border-slate-200 shadow-premium" data-testid="keberadaan-widget">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#DC2626]" /> Keberadaan Pejabat
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full pulse-dot" /> Real-time
            </span>
          </CardTitle>
          <Link to="/keberadaan" className="text-xs font-semibold text-[#DC2626] hover:underline inline-flex items-center gap-1">
            Lihat Detail <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(STATUS_META).map(([status, meta]) => {
            const count = data.counts[status] || 0;
            const Icon = meta.icon;
            return (
              <div key={status} className={`p-3 rounded-xl border-2 ${colorCls[meta.color]}`}>
                <div className="flex items-center justify-between mb-1">
                  <Icon className="w-4 h-4" />
                  <span className="text-2xl font-extrabold tabular">{count}</span>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider">{status}</div>
              </div>
            );
          })}
        </div>
        {data.pejabat.length > 0 && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
            {data.pejabat.filter((p) => p.keberadaan?.status !== 'Di Kantor').slice(0, 9).map((p) => {
              const k = p.keberadaan;
              const meta = STATUS_META[k?.status] || STATUS_META['Di Kantor'];
              const Icon = meta.icon;
              return (
                <Link to={`/manajemen/pejabat/${p.id}`} key={p.id} className={`p-2 rounded-lg border flex items-center gap-2 ${colorCls[meta.color]} hover:shadow-premium transition-shadow`}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold truncate">{p.nama.split(' ').slice(0, 2).join(' ')}</div>
                    <div className="text-[10px] truncate opacity-80">{k?.detail || k?.status}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgendaTimelineList({ items }) {
  if (!items || items.length === 0) {
    return <EmptyState title="Tidak ada agenda" description="Belum ada kegiatan terjadwal." />;
  }
  return (
    <div className="relative pl-5">
      <span className="absolute left-2 top-0 bottom-0 w-px bg-slate-200" />
      <div className="space-y-3">
        {items.map((a) => (
          <Link to={`/agenda/${a.id}`} key={a.id} className="relative block group">
            <span className="absolute -left-[14px] top-3 w-3 h-3 rounded-full border-2 border-white shadow ring-1 ring-slate-200 bg-[#DC2626] group-hover:scale-110 transition-transform" />
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition-colors">
              <div className="flex flex-col md:w-24">
                <div className="text-base font-bold text-slate-900 tabular">{fmtTime(a.tanggal_mulai)}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">WITA</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-800 truncate">{a.nama_acara}</div>
                <div className="text-xs text-slate-500 truncate">
                  <MapPin className="w-3 h-3 inline -mt-0.5 mr-0.5" />{a.lokasi}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={a.tingkat_urgensi} type="urgensi" />
                <StatusBadge status={a.status} type="agenda" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
